import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps } from 'next';
import { DEFAULT_LOCALE } from '@constants/locales';
import {
  fetchLiveBlogBySlug,
  useFetchLiveBlogBySlug,
} from '@hooks/queries/useFetchLiveBlogBySlug';
import {
  fetchLivePostsByBlog,
  useFetchLivePostsByBlog,
} from '@hooks/queries/useFetchLivePostsByBlog';
import { useState } from 'react';
import { FETCH_LIMIT, SINGLE_PAGE } from '@constants/pagination';
import { LiveBlogDetailsPublic } from '@organisms/frontoffice/LiveBlogDetailsPublic';
import { validateZodSchema } from '@utils/zodSchemas';
import { liveBlogPageZodSchema } from '@utils/zodSchemas/details';
import { QUERY_KEY } from '@constants/queries';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { EmptyStateComponent } from '@molecules/frontoffice/EmptyStateComponent';
import { LoadingStateComponent } from '@molecules/frontoffice/LoadingStateComponent';
import {
  fetchPublicity,
  useFetchPublicity,
} from '@hooks/queries/useFetchPublicity';
import { getPublicLiveBlogBySlug } from '@services/liveBlogs';
import { SEO } from '@organisms/SEO';
import { fetchCategories } from '@hooks/queries/useFetchCategories';
import { fetchPublicConfiguration } from '@hooks/queries/useFetchPublicConfiguration';

export const getServerSideProps: GetServerSideProps = async ({ ...ctx }) => {
  const { data: parsedData } = validateZodSchema(
    liveBlogPageZodSchema,
    ctx.params
  );

  const params = {
    slug: parsedData?.slug ?? '',
    page: SINGLE_PAGE,
    limit: FETCH_LIMIT,
  };

  const paramsPublicity = {
    fetchAll: true,
  };

  const queryClient = new QueryClient();

  const liveBlog = await getPublicLiveBlogBySlug(params.slug);

  if (!liveBlog || !liveBlog.slug) {
    return {
      notFound: true,
    };
  }

  const liveBlogPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_LIVE_BLOG_BY_SLUG, params.slug],
    queryFn: async () => {
      return fetchLiveBlogBySlug(params.slug);
    },
  });

  const livePostsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_LIVE_POSTS, params],
    queryFn: async () => {
      return await fetchLivePostsByBlog(params);
    },
  });

  const publicityPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLICITY, paramsPublicity],
    queryFn: async () => {
      return await fetchPublicity(paramsPublicity);
    },
  });

  const categoriesPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_CATEGORIES, { fetchAll: true }],
    queryFn: async () => {
      return await fetchCategories({ fetchAll: true });
    },
  });

  const configurationsPromise = queryClient.prefetchQuery({
    queryKey: [QUERY_KEY.FETCH_PUBLIC_CONFIGURATION],
    queryFn: async () => {
      return await fetchPublicConfiguration();
    },
  });

  await Promise.all([
    categoriesPromise,
    publicityPromise,
    configurationsPromise,
    livePostsPromise,
    liveBlogPromise,
  ]);

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale ?? DEFAULT_LOCALE, [
        'liveBlogDetails',
        'common',
        'dashboard',
      ])),
      dehydratedState: dehydrate(queryClient),
      slug: parsedData?.slug ?? '',
    },
  };
};

const MainLiveBlogDetailsPage = ({ slug }: { slug: string }) => {
  const [pageNumber, setPageNumber] = useState(1);

  const { data: postsResponse, isFetched: isFetchedPosts } =
    useFetchLivePostsByBlog({
      page: pageNumber,
      limit: FETCH_LIMIT,
      slug: slug,
    });

  const { data: publicity } = useFetchPublicity({
    fetchAll: true,
  });

  const {
    data: liveBlog,
    isFetched,
    isFetching,
  } = useFetchLiveBlogBySlug(slug);

  if ((isFetching && !liveBlog) || (!isFetched && !liveBlog)) {
    return <LoadingStateComponent />;
  }

  if (isFetched && !liveBlog) {
    return <EmptyStateComponent />;
  }

  if (!liveBlog) {
    return <EmptyStateComponent />;
  }

  return (
    <>
      <SEO
        title={liveBlog.title_seo ?? liveBlog.title}
        description={liveBlog.description_seo}
        keywords={liveBlog.keywords_seo}
        image_url={liveBlog.header_image?.key}
        type="article"
        post={{
          title: liveBlog.title_seo ?? liveBlog.title,
          description: liveBlog.description_seo,
          mainImageUrl: liveBlog.header_image?.key,
          publishedAt: liveBlog.publish_at,
        }}
      />
      <LiveBlogDetailsPublic
        isFetched={isFetchedPosts}
        pageNumber={pageNumber}
        publicities={publicity?.data}
        setPageNumber={setPageNumber}
        liveBlog={liveBlog}
        totalPages={postsResponse?.meta.total_pages}
        livePosts={postsResponse?.data ?? []}
      />
    </>
  );
};

export default MainLiveBlogDetailsPage;
